import React, { useState, useMemo, useCallback } from 'react';
import { connect } from 'react-redux';
import { GridLegacy as Grid, LinearProgress } from '@mui/material';

import { Page, Row } from '../../util/Page';

import { modifyRelations } from '../../../redux/actions/buildings';
import getBuildingStaging from '../../../redux/selectors/staging';
import StagingFilterBuilder from '../elements/StagingFilterBuilder';
import { equipTypeFilter, buildConfidenceFilter } from '../elements/filter/EquipFilters';
import { buildIsSelectedFilter, nameFilter } from '../elements/filter/GenericFilters';
import BuildingStagingLoader from '../../loaders/BuildingStagingLoader';
import LocationLoader from '../../loaders/LocationLoader';
import EquipmentLoader from '../../loaders/EquipmentLoader';
import { FilterProvider } from '../contexts/FilterContext';
import { EquipmentSelectorDialog } from '../elements/dialogs/EquipmentSelectorDialog';
import { LocationSelectorDialog } from '../elements/dialogs/LocationSelectorDialog';
import EquipmentRelationList from '../elements/EquipmentRelationList';
import EquipmentRelationshipsCard from '../elements/EquipmentRelationshipsCard';

const RelationshipView = ({
  loaded,
  buildingId,
  locationsByName,
  equipment,
  promotedEquipmentById,
  equipTypes,

  updateFeeds,
}) => {
  const buildingEquipmentByName = useMemo(
    () =>
      Object.fromEntries(
        Object.values(promotedEquipmentById)
          .filter((equip) => equip.buildingId === buildingId)
          .map((equip) => [equip.name, equip])
      ),
    [promotedEquipmentById, buildingId]
  );

  const filterDefs = useMemo(() => [equipTypeFilter, nameFilter], []);

  const selectorFilterDefs = useMemo(
    () => [buildIsSelectedFilter, buildConfidenceFilter, () => nameFilter, () => equipTypeFilter],
    []
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [equipmentSelectorOpen, setEquipmentSelectorOpen] = useState(false);
  const [locationSelectorOpen, setLocationSelectorOpen] = useState(false);
  const [editingEquipment, setEditingEquipment] = useState({});

  const getConfidence = useCallback(
    (e) => {
      if (equipment.get(e.equipId).data['ref_tagger.fed'] === editingEquipment.equipId) {
        return equipment.get(e.equipId).confidences['ref_tagger.fed'];
      }
      return 0;
    },
    [equipment, editingEquipment]
  );

  const onEditLocationTargets = (sourceEquip) => {
    if (locationSelectorOpen) return;

    setEditingEquipment(sourceEquip);
    setLocationSelectorOpen(true);
  };

  const onEditEquipmentTargets = (sourceEquip) => {
    if (equipmentSelectorOpen) return;

    setEditingEquipment(sourceEquip);
    setEquipmentSelectorOpen(true);
  };

  if (!loaded) {
    return (
      <Page>
        <Row>
          <EquipmentLoader key={buildingId} buildingId={buildingId} />
          <LocationLoader key={`ll-${buildingId}`} buildingId={buildingId} />
          <BuildingStagingLoader key={`stg-${buildingId}`} buildingId={buildingId} />
          <LinearProgress color="secondary" className="loading" />
        </Row>
      </Page>
    );
  }

  return (
    <FilterProvider onFilterChange={() => setCurrentPage(1)}>
      <Grid item xs={12}>
        <StagingFilterBuilder filterDefs={filterDefs} />
      </Grid>
      <Grid item xs={12}>
        <EquipmentRelationList
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          equipment={Object.values(buildingEquipmentByName)}
          loading={!loaded}
          renderChild={(e) => (
            <EquipmentRelationshipsCard
              key={`${e.id}-card`}
              buildingId={buildingId}
              equipment={e}
              onEditEquipmentTargets={onEditEquipmentTargets}
              onEditLocationTargets={onEditLocationTargets}
              equipmentByName={buildingEquipmentByName}
            />
          )}
        />
      </Grid>
      <FilterProvider>
        <LocationSelectorDialog
          open={locationSelectorOpen}
          onClose={() => setLocationSelectorOpen(false)}
          loading={!loaded}
          buildingId={buildingId}
          editing={editingEquipment}
          locationsByName={locationsByName}
          title={`Equipment ${editingEquipment?.name}: Edit FEEDS Location`}
          defaultSelection={(e) => e?.feeds_locations ?? []}
          onSubmit={(selectedTargets) => {
            const equipTargets = editingEquipment.feeds_locations;

            const newTargets = selectedTargets
              .filter((name) => !equipTargets.includes(name))
              .map((tname) => ({
                source_name: editingEquipment.name,
                target_name: tname,
              }));
            const removedTargets = equipTargets
              .filter((name) => !selectedTargets.includes(name))
              .map((tname) => ({
                source_name: editingEquipment.name,
                target_name: tname,
              }));

            return updateFeeds(newTargets, removedTargets);
          }}
        />
      </FilterProvider>
      <FilterProvider>
        <EquipmentSelectorDialog
          open={equipmentSelectorOpen}
          onClose={() => setEquipmentSelectorOpen(false)}
          loading={!loaded}
          buildingId={buildingId}
          equipTypes={equipTypes}
          editing={editingEquipment}
          filterDefBuilders={selectorFilterDefs}
          getConfidence={getConfidence}
          promotedEquipment={Object.values(buildingEquipmentByName)}
          title={`Equipment ${editingEquipment?.name}: Edit FEEDS Equipment`}
          defaultSelection={(e) => e?.feeds_equipment ?? []}
          onSubmit={(selectedTargets) => {
            const equipTargets = editingEquipment.feeds_equipment;

            const newTargets = selectedTargets
              .filter((name) => !equipTargets.includes(name))
              .map((tname) => ({
                source_name: editingEquipment.name,
                target_name: tname,
              }));
            const removedTargets = equipTargets
              .filter((name) => !selectedTargets.includes(name))
              .map((tname) => ({
                source_name: editingEquipment.name,
                target_name: tname,
              }));

            return updateFeeds(newTargets, removedTargets);
          }}
        />
      </FilterProvider>
    </FilterProvider>
  );
};

const mapStateToProps = (state, { buildingId }) => ({
  ...getBuildingStaging(state, buildingId),
  loaded: state.entities.staging.loaded[buildingId] ?? false,
  equipTypes: state.entities.equipTypes.byId,
  promotedEquipmentById: state.entities.equipment.byId ?? {},

  locationsByName: state.entities.buildings.locationsByBuildingIdName[buildingId] ?? {},
  buildingId,
});

const mapDispatchToProps = (dispatch, { buildingId }) => ({
  updateFeeds: (creates, deletes) =>
    dispatch(modifyRelations(buildingId, 'FEEDS', creates, deletes)),
});

export default connect(mapStateToProps, mapDispatchToProps)(RelationshipView);
